// Global Instructions Rule Applied!
//
// AggregationPanel — drag-to-reorder list of dimension selects. Mirrors the
// SortPanel pattern (react-dnd + faGripVertical handle + add/remove rows).
// Each row picks one dimension; the order of rows is the user's grouping order.

import { useCallback, useMemo, useRef } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Select, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripVertical, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import Button from '../../../../core/components/Button.jsx'
import {
  FUND_INVESTMENT_AGGREGATION_DIMENSIONS,
  type AggregationDimensionOption
} from './aggregationDimensions.ts'

const AGG_ROW_TYPE = 'fund_investment_aggregation_row'

export interface AggregationDraftRow {
  id: string
  dimension?: string
}

let aggRowIdSeq = 0
export function createAggregationDraftRow(seed?: Partial<AggregationDraftRow>): AggregationDraftRow {
  aggRowIdSeq += 1
  return {
    id: `agg-row-${aggRowIdSeq}-${Date.now()}`,
    dimension: seed?.dimension
  }
}

export interface AggregationPanelProps {
  value: AggregationDraftRow[]
  onChange: (next: AggregationDraftRow[]) => void
  options?: AggregationDimensionOption[]
}

interface DraggableRowProps {
  row: AggregationDraftRow
  index: number
  rowCount: number
  moveRow: (dragIndex: number, hoverIndex: number) => void
  optionsForRow: (rowId: string) => { value: string; label: string }[]
  updateRow: (id: string, patch: Partial<AggregationDraftRow>) => void
  removeRow: (id: string) => void
}

function DraggableAggregationRow({
  row,
  index,
  rowCount,
  moveRow,
  optionsForRow,
  updateRow,
  removeRow
}: DraggableRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const gripRef = useRef<HTMLSpanElement>(null)

  const [{ isDragging }, drag] = useDrag({
    type: AGG_ROW_TYPE,
    item: { index, id: row.id },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  const [, drop] = useDrop({
    accept: AGG_ROW_TYPE,
    hover: (draggedItem: { index: number; id: string }, monitor) => {
      if (!rowRef.current) return
      const dragIndex = draggedItem.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      const rect = rowRef.current.getBoundingClientRect()
      const middleY = (rect.bottom - rect.top) / 2
      const offset = monitor.getClientOffset()
      if (!offset) return
      const clientY = offset.y - rect.top
      if (dragIndex < hoverIndex && clientY < middleY) return
      if (dragIndex > hoverIndex && clientY > middleY) return
      moveRow(dragIndex, hoverIndex)
      draggedItem.index = hoverIndex
    }
  })

  drag(gripRef)
  drop(rowRef)

  return (
    <div
      ref={rowRef}
      className={`rounded-md border border-gray-200 bg-gray-50/80 p-3 transition-opacity ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <Space direction='vertical' className='w-full' size={6}>
        <div className='flex min-w-0 items-center gap-2'>
          <span
            ref={gripRef}
            className='flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center text-gray-400 active:cursor-grabbing'
            aria-label='Drag to reorder aggregation'
          >
            <FontAwesomeIcon icon={faGripVertical} className='text-sm' />
          </span>
          <Select
            placeholder='Select dimension'
            allowClear
            className='w-full flex-1'
            options={optionsForRow(row.id)}
            value={row.dimension}
            onChange={(v) => updateRow(row.id, { dimension: v || undefined })}
          />
          {rowCount > 1 && (
            <button
              type='button'
              aria-label='Remove aggregation'
              className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-red-600 hover:bg-red-50'
              onClick={() => removeRow(row.id)}
            >
              <FontAwesomeIcon icon={faTrash} className='text-sm' />
            </button>
          )}
        </div>
      </Space>
    </div>
  )
}

export function AggregationPanel({
  value,
  onChange,
  options = FUND_INVESTMENT_AGGREGATION_DIMENSIONS
}: AggregationPanelProps) {
  const draftRows = useMemo<AggregationDraftRow[]>(
    () => (value.length > 0 ? value : [createAggregationDraftRow()]),
    [value]
  )

  const updateRow = useCallback(
    (id: string, patch: Partial<AggregationDraftRow>) => {
      const next = draftRows.map((r) => (r.id === id ? { ...r, ...patch } : r))
      onChange(next)
    },
    [draftRows, onChange]
  )

  const addRow = useCallback(() => {
    onChange([...draftRows, createAggregationDraftRow()])
  }, [draftRows, onChange])

  const removeRow = useCallback(
    (id: string) => {
      const next = draftRows.filter((r) => r.id !== id)
      onChange(next.length > 0 ? next : [createAggregationDraftRow()])
    },
    [draftRows, onChange]
  )

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const next = [...draftRows]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      onChange(next)
    },
    [draftRows, onChange]
  )

  const optionsForRow = useCallback(
    (rowId: string) => {
      const row = draftRows.find((r) => r.id === rowId)
      const takenByOthers = new Set(
        draftRows.filter((r) => r.id !== rowId && r.dimension).map((r) => r.dimension)
      )
      return options
        .filter((o) => !takenByOthers.has(o.value) || row?.dimension === o.value)
        .map((o) => ({ value: o.value, label: o.label }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    },
    [draftRows, options]
  )

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1'>
        <DndProvider backend={HTML5Backend}>
          {draftRows.map((row, index) => (
            <DraggableAggregationRow
              key={row.id}
              row={row}
              index={index}
              rowCount={draftRows.length}
              moveRow={moveRow}
              optionsForRow={optionsForRow}
              updateRow={updateRow}
              removeRow={removeRow}
            />
          ))}
        </DndProvider>
        <Button
          variant='default'
          className='w-full shrink-0 border border-dashed border-gray-300'
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={addRow}
        >
          Add aggregation
        </Button>
      </div>
    </div>
  )
}

/** Helpers — convert between the rows used by the panel and the flat string[] value */
export function aggregationToDraftRows(values: string[] | null | undefined): AggregationDraftRow[] {
  const arr = Array.isArray(values) ? values.filter((v) => typeof v === 'string' && v) : []
  if (arr.length === 0) return [createAggregationDraftRow()]
  return arr.map((v) => createAggregationDraftRow({ dimension: v }))
}

export function draftRowsToAggregation(rows: AggregationDraftRow[]): string[] {
  return rows.map((r) => r.dimension).filter((v): v is string => typeof v === 'string' && v.length > 0)
}
